/**
 * The transport: `dataLayer`, the gtag stub, and the lazily-fetched tag script.
 *
 * Laziness is the whole point of this unit. Creating a client costs nothing but
 * an array and a closure; the ~90KB tag is only fetched when `ensureLoaded()` is
 * first called, at the next idle moment. Commands issued before then queue on
 * `dataLayer`, which is exactly what gtag.js replays when it arrives — so
 * tracking "too early" loses nothing.
 *
 * Load state lives in the closure rather than in module scope, so a test (or a
 * re-init) gets a genuinely fresh client instead of leftovers.
 */
import {
  DEFAULT_LOAD_TIMEOUT_MS,
  TAG_ELEMENT_ID,
  TAG_ORIGIN,
} from './analytics.constants';
import type {
  AnalyticsEventName,
  AnalyticsOutgoingParams,
  MeasurementId,
} from './analytics.types';

/** Queue entries are `arguments` objects, which is the shape gtag.js replays. */
type GtagQueueEntry = IArguments;

export type GtagWindow = Window & {
  dataLayer?: GtagQueueEntry[];
  gtag?: (...args: unknown[]) => void;
};

/**
 * The page a hit is attributed to. Every GA4 hit carries one, and left alone
 * gtag fills it from `location.href` — query string and all. Setting it by hand
 * is what keeps one-time tokens out of every event, not only page views.
 */
export interface GtagPageProperties {
  page_location: string;
  page_title?: string;
  page_referrer?: string;
}

/** Config parameters passed to a GA4 property on `config`. */
export interface GtagConfigParams extends GtagPageProperties {
  send_page_view: boolean;
  app_version: string;
  debug_mode: boolean;
  cookie_domain: string;
}

/**
 * The gtag command surface, one overload per command this module issues. The
 * event overload is bound to {@link AnalyticsEventName}, so an unnamed event
 * cannot reach `dataLayer` even from inside this folder.
 */
export interface GtagCommands {
  (command: 'js', timestamp: Date): void;
  (command: 'config', measurementId: MeasurementId, params: GtagConfigParams): void;
  (command: 'set', properties: GtagPageProperties): void;
  (
    command: 'event',
    name: AnalyticsEventName,
    params: AnalyticsOutgoingParams,
  ): void;
}

export interface GtagClient {
  /** Queue a gtag command. A no-op once the tag has failed to load. */
  readonly command: GtagCommands;
  /** Fetch the tag if it is not on its way already. Safe to call per event. */
  ensureLoaded(): void;
  /** True once the tag could not be fetched — blocked, offline, or 404. */
  readonly hasFailed: boolean;
}

export interface GtagClientOptions {
  /** The GA4 property to report to, and the id the tag script is fetched with. */
  measurementId: MeasurementId;
  loadTimeoutMs?: number;
}

type LoadState = 'unloaded' | 'pending' | 'loaded' | 'failed';

/**
 * Google's own gtag stub. Commands are queued as the live `arguments` object,
 * which is the shape gtag.js expects to find when it replays `dataLayer`.
 */
function createGtagStub(win: GtagWindow): (...args: unknown[]) => void {
  function gtag(): void {
    win.dataLayer?.push(arguments);
  }
  return gtag as (...args: unknown[]) => void;
}

/** Run at the next idle moment, or on the next tick where that does not exist. */
function whenIdle(win: GtagWindow, task: () => void, timeoutMs: number): void {
  const idle = (
    win as unknown as {
      requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => void;
    }
  ).requestIdleCallback;

  if (typeof idle === 'function') {
    idle.call(win, task, { timeout: timeoutMs });
  } else {
    win.setTimeout(task, 0);
  }
}

export function createGtagClient(
  win: GtagWindow,
  options: GtagClientOptions,
): GtagClient {
  const { measurementId } = options;
  const loadTimeoutMs = options.loadTimeoutMs ?? DEFAULT_LOAD_TIMEOUT_MS;
  let loadState: LoadState = 'unloaded';

  win.dataLayer = win.dataLayer || [];
  // Reuse an existing gtag (a container already on the page) rather than
  // shadowing it, so both tags keep working.
  win.gtag = win.gtag || createGtagStub(win);

  const command = ((...args: unknown[]): void => {
    if (loadState === 'failed') return;
    win.gtag?.(...args);
  }) as GtagCommands;

  function injectTag(): void {
    const doc = win.document;
    if (doc.getElementById(TAG_ELEMENT_ID)) {
      loadState = 'loaded';
      return;
    }

    const script = doc.createElement('script');
    script.id = TAG_ELEMENT_ID;
    script.async = true;
    script.src = `${TAG_ORIGIN}?id=${encodeURIComponent(measurementId)}`;
    script.onload = () => {
      loadState = 'loaded';
    };
    // Blocked by an extension, or a network with no route to Google. Stop
    // queueing so a long session cannot grow `dataLayer` without bound.
    script.onerror = () => {
      loadState = 'failed';
    };

    (doc.head || doc.body || doc.documentElement).appendChild(script);
  }

  return {
    command,
    ensureLoaded(): void {
      if (loadState !== 'unloaded') return;
      loadState = 'pending';
      whenIdle(win, injectTag, loadTimeoutMs);
    },
    get hasFailed(): boolean {
      return loadState === 'failed';
    },
  };
}
